"use client"

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from "react"
import { useRouter, usePathname } from "next/navigation"
import { useQueryClient } from "@tanstack/react-query"
import type { AskScopeRequest, ChatChartCard, Conversation, ChatMessage, EntityCard } from "./types"
import { type NavContext } from "./tools"
import { useChatHotkey } from "./use-chat-hotkey"
import { deriveConversationTitle } from "./conversation-title"
import { ApiClientError, fetchApi, getAccessToken, getWorkspaceId } from "@/lib/api/client"
import { API_CONFIG } from "@/lib/api/config"
import { resolveToolLabel } from "./tool-display"
import { vishingChatContext } from './vishing-context'
import { useWorkspaceStore } from '@/stores/workspace-store'
import {
  parseAiErrorBody,
  resolveAiHeadline,
  sanitizeAiError,
  toAiError,
  type AiError,
} from "@/lib/ai/errors"
import { useTranslation } from "@/lib/i18n"
import { exportFileName } from "./export-filename"

type Translate = (key: string) => string

/**
 * The line a failure leaves in the transcript, in the admin's language:
 * "**Error:** <headline>". The headline is still SELECTED by the error's code
 * (resolveAiHeadline) — only its language changes, never its source.
 */
function transcriptErrorLine(error: AiError, t: Translate): string {
  if (error.errorCode === 'AI_VISHING_VOICE_RESIDENCY')
    return t('vishing.ai.residencyUnavailable')
  return `**${t("aiChat.transcript.errorLabel")}** ${resolveAiHeadline(error, t)}`
}

// ─── Streaming tool-call shape (lightweight, for UI only) ────────────────────
export interface StreamingToolCall {
  toolName: string
  /** Server-authored human label for `toolName`, when the backend sent one. */
  toolLabel?: string
  status: "executing" | "Completed" | "error"
  success?: boolean
  summary?: string
  /**
   * What the tool created or changed, when it is something worth showing.
   * Drives the rich result card (name, facts, open link, inline preview)
   * instead of the one-line status chip.
   */
  entity?: EntityCard | null
  /** A series the tool returned, to draw (vCRO wave 4). */
  chart?: ChatChartCard | null
}

export type CompanionMode = "closed" | "pip" | "drawer" | "maximized"

interface ChatRequestOwner {
  token: symbol
  abort: AbortController
}

// ─── Context value ───────────────────────────────────────────────────────────
export interface ChatContextValue {
  // Conversations
  conversations: Conversation[]
  activeId: string | null
  fetchConversations: (search?: string) => void
  loadConversation: (id: string) => void
  handleNewChat: () => void
  handleDelete: (id?: string) => void
  handleRename: (title: string) => void
  handleTogglePin: (id?: string) => void
  handleToggleStar: (id?: string) => void

  // Messages
  messages: ChatMessage[]
  inputValue: string
  setInputValue: (v: string) => void
  sendMessage: (text: string) => Promise<void>
  /**
   * Ask from a page, about the page (vCRO wave 4, "Ask vCRO"): opens the
   * drawer, starts a fresh conversation and sends `text` with the scope and
   * the page's context. Follow-ups in that conversation keep the scope; a new
   * chat or another conversation drops it.
   */
  askInScope: (text: string, request: AskScopeRequest) => Promise<void>
  handleStop: () => void
  handleRegenerate: () => void
  handleEditMessage: (id: string) => void

  // Streaming
  isStreaming: boolean
  streamingContent: string
  streamingToolCalls: StreamingToolCall[]

  // Utility
  handleExport: () => void
  handleCopyAll: () => void
  handleSuggestion: (text: string) => void
  apiKeyMissing: boolean
  /**
   * The last AI failure, typed and sanitized (`lib/ai/errors.ts`).
   *
   * Exists because the two error paths in `sendMessage` used to render server
   * text directly: the non-OK branch read `errData.error`, which for a NestJS
   * `BadRequestException` is the literal string "Bad Request" — so the monthly
   * credit ceiling (`quota_exhausted_use_own_key`, returned as HTTP 400, never
   * 429) reached the customer as "**Error:** Bad Request" — and the in-stream
   * `error` frame path rendered the provider's own text verbatim.
   *
   * Surfaces branch on `.errorCode`; the raw detail stays on `.technical` for
   * the collapsed disclosure and never reaches a sentence.
   */
  chatError: AiError | null
  clearChatError: () => void

  // Verification
  verificationOpen: boolean
  pendingVerificationTool: string
  /** Server-authored human label for `pendingVerificationTool`, when sent. */
  pendingVerificationToolLabel: string
  pendingVerificationInputRef: MutableRefObject<Record<string, unknown>>
  pendingVerificationRequirement: 'confirm' | 'step-up'
  pendingVerificationFactors: Array<'passkey' | 'password'>
  pendingVerificationReason: string
  pendingVerificationMessage: string
  handleVerified: () => void
  handleVerificationCancel: () => void

  // Chat sidebar (within the chat panel)
  chatSidebarOpen: boolean
  setChatSidebarOpen: (v: boolean | ((prev: boolean) => boolean)) => void

  // Companion mode
  companionMode: CompanionMode
  setCompanionMode: (mode: CompanionMode) => void

  // Drawer width (for docked mode)
  drawerWidth: number
  setDrawerWidth: (w: number) => void

  // Auto-navigation
  autoNavigate: boolean
  setAutoNavigate: (v: boolean) => void

  // Step-up auth token (verify-tier tools — valid for 5 min)
  stepUpToken: string | null
  stepUpTokenExpiresAt: string | null
  setStepUpToken: (token: string, expiresAt: string) => void
  clearStepUpToken: () => void
}

export const ChatContext = createContext<ChatContextValue | null>(null)

export function useChatContext() {
  const ctx = useContext(ChatContext)
  if (!ctx) throw new Error("useChatContext must be used within ChatProvider")
  return ctx
}

/**
 * The two fields the PAGE LAYOUTS need — deliberately split out of
 * `ChatContextValue`.
 *
 * `/recipients`, `/er`, `/esa` and `/reports` each wrap their whole page in a
 * layout that reserves margin for the chat drawer. They needed exactly
 * `companionMode` + `drawerWidth`, but read them from the full chat context —
 * which also carries `inputValue` and `streamingContent`. Those change on every
 * KEYSTROKE and every streamed TOKEN, and a context consumer re-renders whenever
 * the context value changes, so typing one character into the chat composer
 * re-rendered the entire recipients page: sidebar, data table, every editable
 * cell. Sentry HFV3-FRONTEND-8 is a "Maximum update depth exceeded" thrown out
 * of that storm, with `/recipients` as the transaction and the composer's
 * `onChange` in the stack.
 *
 * Splitting is the fix rather than just memoising: a memo cannot help when the
 * value legitimately changes: `inputValue` really is different on every
 * keystroke. Only narrowing what the layouts subscribe to breaks the link.
 */
export interface ChatLayoutValue {
  companionMode: CompanionMode
  drawerWidth: number
}

export const ChatLayoutContext = createContext<ChatLayoutValue | null>(null)

/**
 * Use this — NOT `useChatContext()` — anywhere that only needs the drawer
 * geometry. Reaching for the full context re-subscribes that component to
 * per-keystroke updates and silently reopens HFV3-FRONTEND-8.
 */
export function useChatLayout() {
  const ctx = useContext(ChatLayoutContext)
  if (!ctx) throw new Error("useChatLayout must be used within ChatProvider")
  return ctx
}

// ─── Provider ────────────────────────────────────────────────────────────────
export function ChatProvider({ children }: { children: ReactNode }) {
  const router = useRouter()
  const pathname = usePathname()
  // Used to refresh cached lists after the assistant writes something.
  const queryClient = useQueryClient()
  // The transcript's own lines (a failure, "stopped", "cut short") are worded
  // when they are written. Read through a ref, so changing the language does
  // not re-create sendMessage under a stream that is still running.
  const { t } = useTranslation()
  const tRef = useRef<Translate>(t)
  useEffect(() => {
    tRef.current = t
  }, [t])

  // ── Conversations ────────────────────────────────────────────────────────
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [activeId, _setActiveId] = useState<string | null>(null)
  // Bumped on every change of the active conversation and at the start of
  // every loadConversation, so the on-mount restore and a slow thread load
  // can tell the user has moved on while they were loading.
  const activeIdVersion = useRef(0)

  // Wrap setActiveId to always persist to localStorage
  const setActiveId = useCallback((id: string | null) => {
    activeIdVersion.current += 1
    _setActiveId(id)
    if (typeof window !== "undefined") {
      if (id) {
        localStorage.setItem("ai-chat-active-id", id)
      } else {
        localStorage.removeItem("ai-chat-active-id")
      }
    }
  }, [])

  // ── Messages ─────────────────────────────────────────────────────────────
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState("")

  // ── Streaming ────────────────────────────────────────────────────────────
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingContent, setStreamingContent] = useState("")
  const [streamingToolCalls, setStreamingToolCalls] = useState<StreamingToolCall[]>([])
  const requestOwnerRef = useRef<ChatRequestOwner | null>(null)
  const toolCallsRef = useRef<StreamingToolCall[]>([])

  useEffect(() => () => {
    const owner = requestOwnerRef.current
    requestOwnerRef.current = null
    owner?.abort.abort()
  }, [])

  // ── Verification ─────────────────────────────────────────────────────────
  const [verificationOpen, setVerificationOpen] = useState(false)
  const [pendingVerificationTool, setPendingVerificationTool] = useState("")
  const [pendingVerificationToolLabel, setPendingVerificationToolLabel] = useState("")
  const pendingVerificationInputRef = useRef<Record<string, unknown>>({})
  const [pendingVerificationRequirement, setPendingVerificationRequirement] = useState<'confirm' | 'step-up'>('confirm')
  const [pendingVerificationFactors, setPendingVerificationFactors] = useState<Array<'passkey' | 'password'>>([])
  const [pendingVerificationReason, setPendingVerificationReason] = useState<string>('')
  const [pendingVerificationMessage, setPendingVerificationMessage] = useState<string>('')

  // ── Deferred navigation ──────────────────────────────────────────────────
  // Accumulate navigate events during streaming; execute only the final one
  // when the stream ends. Explicit navigate_to_page always wins over implicit.
  const pendingNavRef = useRef<{
    path: string
    context?: NavContext
    explicit: boolean
  } | null>(null)

  // ── Chat sidebar (inside chat panel) ─────────────────────────────────────
  const [chatSidebarOpen, setChatSidebarOpen] = useState(true)

  // Hydrate chatSidebarOpen from localStorage after mount
  const chatSidebarHydrated = useRef(false)
  useEffect(() => {
    if (!chatSidebarHydrated.current) {
      chatSidebarHydrated.current = true
      const saved = localStorage.getItem("ai-chat-sidebar")
      if (saved !== null) setChatSidebarOpen(saved === "true")
    }
  }, [])

  useEffect(() => {
    localStorage.setItem("ai-chat-sidebar", String(chatSidebarOpen))
  }, [chatSidebarOpen])

  // ── Companion mode ───────────────────────────────────────────────────────
  const [companionMode, setCompanionMode] = useState<CompanionMode>("closed")
  const companionModeHydrated = useRef(false)

  // Hydrate from localStorage after mount to avoid SSR/client mismatch
  useEffect(() => {
    if (!companionModeHydrated.current) {
      companionModeHydrated.current = true
      const saved = localStorage.getItem("ai-chat-companion-mode")
      if (saved === "pip" || saved === "drawer" || saved === "maximized") {
        setCompanionMode(saved)
      }
    }
  }, [])

  useEffect(() => {
    localStorage.setItem("ai-chat-companion-mode", companionMode)
  }, [companionMode])

  // ── Global Cmd+K hotkey to toggle the chat drawer ────────────────────────
  useChatHotkey(setCompanionMode, companionMode)

  // ── Drawer width ────────────────────────────────────────────────────────
  const DRAWER_DEFAULT = 420
  const DRAWER_MIN = 320
  const DRAWER_MAX = 700
  const [drawerWidth, setDrawerWidthRaw] = useState(DRAWER_DEFAULT)
  const setDrawerWidth = useCallback((w: number) => {
    const clamped = Math.min(DRAWER_MAX, Math.max(DRAWER_MIN, w))
    setDrawerWidthRaw(clamped)
    localStorage.setItem("ai-chat-drawer-width", String(clamped))
  }, [])

  // Hydrate drawer width
  useEffect(() => {
    const saved = localStorage.getItem("ai-chat-drawer-width")
    if (saved) {
      const n = parseInt(saved, 10)
      if (!isNaN(n)) setDrawerWidthRaw(Math.min(DRAWER_MAX, Math.max(DRAWER_MIN, n)))
    }
  }, [])

  // Close chat when feedback widget opens (cross-widget coordination)
  useEffect(() => {
    const handler = () => setCompanionMode("closed")
    window.addEventListener("widget:close-chat", handler)
    return () => window.removeEventListener("widget:close-chat", handler)
  }, [])

  // ── Auto-navigation ──────────────────────────────────────────────────────
  const [autoNavigate, setAutoNavigate] = useState(true)

  // Hydrate autoNavigate from localStorage after mount
  const autoNavigateHydrated = useRef(false)
  useEffect(() => {
    if (!autoNavigateHydrated.current) {
      autoNavigateHydrated.current = true
      const saved = localStorage.getItem("ai-chat-auto-navigate")
      if (saved !== null) setAutoNavigate(saved === "true")
    }
  }, [])

  useEffect(() => {
    localStorage.setItem("ai-chat-auto-navigate", String(autoNavigate))
  }, [autoNavigate])

  const [apiKeyMissing, setApiKeyMissing] = useState(false)
  const [chatError, setChatError] = useState<AiError | null>(null)
  const clearChatError = useCallback(() => setChatError(null), [])

  // ── Step-up auth token ───────────────────────────────────────────────────
  const [stepUpToken, setStepUpTokenState] = useState<string | null>(null)
  const [stepUpTokenExpiresAt, setStepUpExpiresAt] = useState<string | null>(null)
  const stepUpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // FIX: render-flush race in step-up flow
  // Root cause: sendMessage is memoized and captures stepUpToken from React state.
  // When setStepUpToken() is called, it schedules a state update but the render
  // hasn't committed yet. If sendMessage fires on the same tick (as it does from
  // verification-dialog.tsx after stepUpVerify succeeds), its closure captures
  // the OLD (null) token, causing the BE to return verification_required again.
  // Fix: keep a ref mirror that is written synchronously BEFORE the state update.
  // sendMessage reads from the ref — no render boundary dependency.
  // Date: 2026-05-05
  const stepUpTokenRef = useRef<string | null>(null)

  // ── Ask scope (vCRO wave 4, "Ask vCRO") ─────────────────────────────────
  // The scope and page context of the conversation a page opened. A ref, read
  // as the request goes out (like the step-up token), so the first message —
  // sent on the same tick the scope is set — carries it. Cleared by a new chat
  // and by opening another conversation, so an ordinary chat is never
  // silently narrowed to one module.
  const askScopeRef = useRef<AskScopeRequest | null>(null)

  const setStepUpToken = useCallback((token: string, expiresAt: string) => {
    // Cancel any existing auto-clear timer before installing a new token
    if (stepUpTimerRef.current !== null) {
      clearTimeout(stepUpTimerRef.current)
      stepUpTimerRef.current = null
    }
    // Write ref first (synchronous) so the token is visible to sendMessage
    // immediately, without waiting for React to commit the state update.
    stepUpTokenRef.current = token
    setStepUpTokenState(token)
    setStepUpExpiresAt(expiresAt)
    const ttl = new Date(expiresAt).getTime() - Date.now()
    if (ttl > 0) {
      stepUpTimerRef.current = setTimeout(() => {
        stepUpTokenRef.current = null
        setStepUpTokenState(null)
        setStepUpExpiresAt(null)
        stepUpTimerRef.current = null
      }, ttl)
    }
  }, [])

  const clearStepUpToken = useCallback(() => {
    if (stepUpTimerRef.current !== null) {
      clearTimeout(stepUpTimerRef.current)
      stepUpTimerRef.current = null
    }
    stepUpTokenRef.current = null
    setStepUpTokenState(null)
    setStepUpExpiresAt(null)
  }, [])

  // ── Fetch conversations ──────────────────────────────────────────────────
  const fetchConversations = useCallback(async (search?: string) => {
    if (requestOwnerRef.current) return
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : ""
      const data = await fetchApi<{ conversations?: Conversation[] }>(`/ai-chat/conversations${params}`)
      if (requestOwnerRef.current) return
      setConversations(data.conversations || [])
    } catch {
      // silent
    }
  }, [])

  // Defer initial conversation fetch until the chat companion is opened,
  // so pages that don't use the chat (e.g. /settings) load faster. After
  // first fetch, refresh on every closed → open transition so the drawer
  // never renders against stale-empty state on subsequent opens.
  const hasFetchedConversations = useRef(false)
  const prevCompanionMode = useRef<CompanionMode>("closed")
  useEffect(() => {
    const wasClosed = prevCompanionMode.current === "closed"
    const isOpenNow = companionMode !== "closed"
    prevCompanionMode.current = companionMode
    if (isOpenNow && (wasClosed || !hasFetchedConversations.current)) {
      hasFetchedConversations.current = true
      fetchConversations()
    }
  }, [companionMode, fetchConversations])

  // ── Restore the last conversation on mount (survives page refresh) ──────
  // #832 — the saved conversation becomes active only TOGETHER with its
  // messages. It used to be restored in two effects: one set the id, the
  // other reloaded the messages — but both ran after the first render, when
  // `activeId` was still null, so the reload never happened. The sidebar then
  // highlighted the old thread while the main pane showed the welcome screen,
  // and a new question was posted into the old thread. Until the messages
  // arrive nothing is active, so the welcome screen starts a new conversation;
  // a restore that lands after the user has moved on (sent a message, opened
  // another thread, started a new chat) is dropped.
  //
  // This provider sits in the ROOT layout, so the restore runs on every hard
  // page load, on every route, with the chat closed. Three rules follow:
  // - No session, no request. A GET without a token 401s, the refresh fails,
  //   and fetchApi then signs the visitor out to /login from whatever page
  //   they were on. The saved id is kept for when they sign back in.
  // - Restore only a thread of the workspace requests now go to; forget any
  //   other. The API now answers another workspace's thread with a 404 (it
  //   is forgotten below); the workspaceId check stays as the second line.
  // - Forget the id only when the API says the conversation is gone (404). A
  //   network error, a 5xx or a 429 during a deploy activates nothing and
  //   keeps the id for the next load.
  const hasRestored = useRef(false)
  useEffect(() => {
    if (hasRestored.current) return
    hasRestored.current = true
    const saved = localStorage.getItem("ai-chat-active-id")
    if (!saved) return
    if (!getAccessToken() || !getWorkspaceId()) return
    const version = activeIdVersion.current
    const movedOn = () =>
      activeIdVersion.current !== version || requestOwnerRef.current !== null
    fetchApi<{ workspaceId?: string; messages?: ChatMessage[] }>(`/ai-chat/conversations/${saved}`)
      .then((data) => {
        if (movedOn()) return
        if (data?.workspaceId === getWorkspaceId() && data.messages?.length) {
          setMessages(data.messages)
          setActiveId(saved)
        } else {
          // Another workspace's thread, or nothing to show: start from the
          // welcome screen, unselected.
          setActiveId(null)
        }
      })
      .catch((err: unknown) => {
        // Only a deleted conversation is forgotten.
        if (movedOn()) return
        if (err instanceof ApiClientError && err.statusCode === 404) setActiveId(null)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Load conversation ────────────────────────────────────────────────────
  const loadConversation = useCallback(async (id: string) => {
    if (requestOwnerRef.current) return
    // Last click wins: each load takes a new version, and an answer that
    // returns after another load, a new chat or a send has moved the active
    // conversation on is dropped — or a slow thread A replaced thread B,
    // clicked after it, and the next question went into A.
    const version = ++activeIdVersion.current
    try {
      const data = await fetchApi<{ messages?: ChatMessage[] }>(`/ai-chat/conversations/${id}`)
      if (requestOwnerRef.current || activeIdVersion.current !== version) return
      askScopeRef.current = null
      setMessages(data.messages || [])
      setActiveId(id)
    } catch {
      // silent
    }
  }, [])

  // ── New chat ─────────────────────────────────────────────────────────────
  const handleNewChat = useCallback(() => {
    if (requestOwnerRef.current) return
    // #106 — lazy new chat: do NOT pre-create an untitled server conversation.
    // Pre-creating one and setting it active meant sendMessage's
    // create-with-title branch (`if (!conversationId)`) was skipped, so every
    // chat started by typing stayed "New Conversation"; it also littered
    // history with empty rows. Reset to a fresh unsaved state — the first sent
    // message now creates a conversation titled from that message, exactly like
    // the quick-prompt path that already worked.
    askScopeRef.current = null
    setActiveId(null)
    setMessages([])
    setStreamingContent("")
    setStreamingToolCalls([])
  }, [])

  // ── Send message (SSE streaming) ─────────────────────────────────────────
  const sendMessage = useCallback(
    async (text: string, options?: { fresh?: boolean }) => {
      if (!text.trim() || requestOwnerRef.current) return

      const owner: ChatRequestOwner = {
        token: Symbol("chat-request"),
        abort: new AbortController(),
      }
      requestOwnerRef.current = owner
      setIsStreaming(true)
      setStreamingContent("")
      setStreamingToolCalls([])
      toolCallsRef.current = []
      pendingNavRef.current = null
      setApiKeyMissing(false)
      setChatError(null)
      const ownsRequest = () => requestOwnerRef.current === owner
      const mayWrite = () => ownsRequest() && !owner.abort.signal.aborted
      // `fresh`: askInScope cleared the conversation on this same tick, so the
      // captured `activeId` is stale; start a new one.
      let conversationId = options?.fresh ? null : activeId
      const askScope = askScopeRef.current

      // ── Streamed-answer state ────────────────────────────────────────────
      // Declared OUTSIDE the try so the catch/abort paths can still commit
      // what was received.
      //
      // The assistant message used to be appended ONLY inside `case "done"`.
      // Any stream that ended without that frame — server restart, proxy
      // timeout, dropped connection, mid-stream error — silently discarded
      // everything already streamed: the user watched an answer appear on
      // screen and then vanish, with no error and nothing persisted.
      //
      // `committed` + commitAssistant() make the rule explicit: the text we
      // showed is the text we keep, on every exit path, exactly once.
      let fullText = ""
      let committed = false
      // Did any tool this turn actually change stored data? The server sets
      // `mutated` on the tool_result frame from the tool's own declared route
      // (see api tools/tool-mutation.ts). Request-scoped rather than a ref:
      // two overlapping turns must not see each other's writes.
      let mutatedThisTurn = false
      const commitAssistant = () => {
        if (committed || !ownsRequest()) return
        committed = true
        if (!fullText && toolCallsRef.current.length === 0) return
        const toolCalls =
          toolCallsRef.current.length > 0
            ? toolCallsRef.current.map((tc, i) => ({
                id: `tc-${Date.now()}-${i}`,
                toolName: tc.toolName,
                toolLabel: tc.toolLabel,
                input: {},
                status: (tc.status === "Completed" ? "Completed" : "error") as
                  | "Completed"
                  | "error",
                result: {
                  success: tc.success ?? true,
                  data: undefined,
                  error: tc.success === false ? tc.summary : undefined,
                },
                summary: tc.summary,
                entity: tc.entity ?? null,
                chart: tc.chart ?? null,
              }))
            : undefined
        const assistantMsg: ChatMessage = {
          id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          role: "assistant",
          content: fullText,
          toolCalls,
          createdAt: new Date().toISOString(),
        }
        setMessages((prev) => [...prev, assistantMsg])
      }

      try {
        if (!conversationId) {
          const conv = await fetchApi<Conversation>("/ai-chat/conversations", {
            method: "POST",
            // #1872: a cleaned-up, word-boundary title instead of a raw
            // character slice — see conversation-title.ts for why this is
            // the frontend-only fix (an LLM-titling PATCH call would need a
            // new backend endpoint outside this change's scope).
            body: JSON.stringify({ title: deriveConversationTitle(text) }),
            signal: owner.abort.signal,
          })
          if (!mayWrite()) return
          setConversations((prev) => [conv, ...prev])
          setActiveId(conv.id)
          conversationId = conv.id
        }

        if (!mayWrite()) return
        const userMsg: ChatMessage = {
          id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          role: "user",
          content: text,
          createdAt: new Date().toISOString(),
        }
        setMessages((prev) => [...prev, userMsg])
        setInputValue("")

        // NOTE: Raw fetch() is intentional here — this is an SSE streaming
        // endpoint that returns a ReadableStream, not JSON. fetchApi() auto-parses
        // JSON responses and cannot handle streaming bodies.
        const streamUrl = `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}/ai-chat/stream`

        // Build headers — auth + workspace headers required by the BE. Through
        // the same accessors fetchApi uses, so the stream and every other
        // request name one session and one workspace (getWorkspaceId() also
        // recovers the workspace from the persisted store; the raw key did not).
        const streamHeaders: Record<string, string> = { "Content-Type": "application/json" }
        const token = getAccessToken()
        if (token) streamHeaders["Authorization"] = `Bearer ${token}`
        const wsId = getWorkspaceId()
        if (wsId) streamHeaders["x-workspace-id"] = wsId
        const pageContext = vishingChatContext(pathname, window.location.search, useWorkspaceStore.getState().activeDomainId)
        if (pageContext?.domainId) streamHeaders['x-domain-id'] = pageContext.domainId

        // Read from ref (not captured state) to get the token written by
        // setStepUpToken on the same synchronous tick — no render flush needed.
        const attachedStepUpToken = stepUpTokenRef.current

        // One-shot on the client too, mirroring the server.
        //
        // A token is bound to ONE tool + input and the executor burns its jti on
        // use, so it can never authorise a second action. Holding it for the full
        // 5-minute TTL and attaching it to every later message therefore cannot
        // help — but it does harm: the next destructive request ships a token the
        // server can only answer with APPROVAL_MISMATCH (different action) or
        // APPROVAL_ALREADY_USED (same one, spent). Both are hard errors rather
        // than a fresh verification prompt, so the user was dead-ended out of
        // every destructive action until the TTL expired.
        //
        // Clearing it as it goes out means the next such request arrives with no
        // token and gets the verification prompt it should have got all along.
        if (attachedStepUpToken) clearStepUpToken()

        const res = await fetch(streamUrl, {
          method: "POST",
          headers: streamHeaders,
          body: JSON.stringify({
            conversationId,
            message: text,
            ...(pageContext ? { moduleContext: pageContext.moduleContext } : {}),
            ...(attachedStepUpToken ? { stepUpToken: attachedStepUpToken } : {}),
            ...(askScope ? { scope: askScope.scope, context: askScope.context } : {}),
          }),
          signal: owner.abort.signal,
        })

        if (!mayWrite()) return

        if (!res.ok) {
          const errBody = await res.json().catch(() => null)
          if (!mayWrite()) return

          // Classify on `errorCode`, STATUS-AGNOSTIC. The credit ceiling is a
          // BadRequestException and therefore arrives as HTTP 400, so anything
          // that branches on status first misses the most important case in the
          // whole vocabulary. `errData.error` — which this used to render — is
          // Nest's generic reason phrase ("Bad Request"), not a message.
          const aiErr = sanitizeAiError(parseAiErrorBody(res.status, errBody))
          setChatError(aiErr)

          // 503 is the transport's way of saying "no provider configured", but
          // an explicit code outranks it: a 400 credit ceiling must NOT be
          // mistaken for a missing key, and a coded 503 says so itself.
          if (
            aiErr.errorCode === "no_provider_key" ||
            (res.status === 503 && aiErr.errorCode === null)
          ) {
            setApiKeyMissing(true)
          }

          const errMsg: ChatMessage = {
            id: `msg-${Date.now()}-err`,
            role: "assistant",
            content: transcriptErrorLine(aiErr, tRef.current),
            createdAt: new Date().toISOString(),
          }
          setMessages((prev) => [...prev, errMsg])
          return
        }

        const reader = res.body?.getReader()
        if (!reader) return

        const decoder = new TextDecoder()
        let buffer = ""

        while (true) {
          const { done, value } = await reader.read()
          if (!mayWrite()) return
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split("\n")
          buffer = lines.pop() || ""

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue
            const jsonStr = line.slice(6).trim()
            if (!jsonStr) continue

            try {
              const event = JSON.parse(jsonStr)

              switch (event.type) {
                case "text":
                  fullText += event.content
                  setStreamingContent(fullText)
                  break

                case "tool_call": {
                  const newTc = {
                    toolName: event.toolName,
                    toolLabel: event.toolLabel as string | undefined,
                    status: "executing" as const,
                  }
                  toolCallsRef.current = [...toolCallsRef.current, newTc]
                  setStreamingToolCalls((prev) => [...prev, newTc])
                  break
                }

                case "tool_result": {
                  // Only a SUCCESSFUL write makes the cache stale. A failed
                  // one changed nothing, and refetching after it would imply
                  // to the user that something happened.
                  if (event.success && (event as { mutated?: boolean }).mutated) {
                    mutatedThisTurn = true
                  }
                  toolCallsRef.current = toolCallsRef.current.map((tc) =>
                    tc.toolName === event.toolName && tc.status === "executing"
                      ? {
                          ...tc,
                          status: "Completed" as const,
                          success: event.success,
                          summary: event.summary,
                          toolLabel: (event.toolLabel as string | undefined) ?? tc.toolLabel,
                          entity: (event as { entity?: StreamingToolCall["entity"] }).entity ?? null,
                          chart: (event as { chart?: StreamingToolCall["chart"] }).chart ?? null,
                        }
                      : tc
                  )
                  setStreamingToolCalls((prev) =>
                    prev.map((tc) =>
                      tc.toolName === event.toolName && tc.status === "executing"
                        ? {
                            ...tc,
                            status: "Completed",
                            success: event.success,
                            summary: event.summary,
                            toolLabel: (event.toolLabel as string | undefined) ?? tc.toolLabel,
                            entity: (event as { entity?: StreamingToolCall["entity"] }).entity ?? null,
                            chart: (event as { chart?: StreamingToolCall["chart"] }).chart ?? null,
                          }
                        : tc
                    )
                  )
                  break
                }

                case "done": {
                  commitAssistant()
                  setStreamingContent("")
                  setStreamingToolCalls([])
                  toolCallsRef.current = []

                  // Execute deferred navigation — single router.push at stream end
                  const nav = pendingNavRef.current
                  if (nav) {
                    pendingNavRef.current = null
                    const navCtx = nav.context

                    if (pathname !== nav.path) {
                      // Cross-page: write context to sessionStorage (consumed
                      // once on the target page mount, then deleted).
                      if (navCtx) {
                        if (navCtx.columnFilters?.length) {
                          sessionStorage.setItem(
                            "ai-nav-column-filters",
                            JSON.stringify(navCtx.columnFilters)
                          )
                        }
                        if (navCtx.globalFilter) {
                          sessionStorage.setItem("ai-nav-global-filter", navCtx.globalFilter)
                        }
                        if (navCtx.focusEntity) {
                          sessionStorage.setItem(
                            "ai-nav-focus",
                            JSON.stringify(navCtx.focusEntity)
                          )
                        }
                      }
                      setCompanionMode("pip")
                      router.push(nav.path)
                    } else if (navCtx) {
                      // Same-page: dispatch event with context directly attached
                      window.dispatchEvent(
                        new CustomEvent("ai-nav-context", { detail: navCtx })
                      )
                    }
                  }
                  break
                }

                case "verification_required": {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const vEvent = event as any
                  setPendingVerificationTool(vEvent.toolName)
                  setPendingVerificationToolLabel(vEvent.toolLabel ?? '')
                  pendingVerificationInputRef.current = vEvent.input || {}
                  setPendingVerificationRequirement(vEvent.requirement ?? 'confirm')
                  setPendingVerificationFactors(vEvent.factors ?? [])
                  setPendingVerificationReason(vEvent.verifyReason ?? vEvent.message ?? '')
                  setPendingVerificationMessage(vEvent.message ?? '')
                  setVerificationOpen(true)
                  break
                }

                case "navigate": {
                  const targetPath = event.path as string
                  const navContext = event.context as NavContext | undefined
                  const isExplicit = event.explicit === true

                  // Accumulate navigate events — execute only the final one when
                  // the stream ends. Explicit navigate_to_page always takes
                  // priority; among implicit navigations the last one wins.
                  // Storage writes are deferred to the "done" handler to avoid
                  // polluting localStorage with intermediate tool contexts.
                  if (autoNavigate && targetPath) {
                    const pending = pendingNavRef.current
                    if (isExplicit || !pending?.explicit) {
                      pendingNavRef.current = { path: targetPath, context: navContext, explicit: isExplicit }
                    }
                  }
                  break
                }

                case "error": {
                  // Keep whatever already streamed. Discarding it left the
                  // user with only "Error:" and no way to tell whether the
                  // work described in the partial answer had happened.
                  commitAssistant()
                  setStreamingContent("")
                  setStreamingToolCalls([])
                  toolCallsRef.current = []
                  // `event.message` is frequently the provider's own text
                  // ("<model>: 429 RESOURCE_EXHAUSTED at <endpoint>"). Select a
                  // headline instead; the raw detail rides on `.technical`.
                  const frameErr = sanitizeAiError(parseAiErrorBody(null, event))
                  setChatError(frameErr)
                  const errMsg: ChatMessage = {
                    id: `msg-${Date.now()}-err`,
                    role: "assistant",
                    content: transcriptErrorLine(frameErr, tRef.current),
                    createdAt: new Date().toISOString(),
                  }
                  setMessages((prev) => [...prev, errMsg])
                  break
                }
              }
            } catch {
              // malformed SSE line
            }
          }
        }
        // The reader closed. If no `done` frame ever arrived — server restart,
        // proxy timeout, dropped connection — the answer is still in fullText
        // and was previously discarded here without a trace.
        if (!committed && (fullText || toolCallsRef.current.length > 0)) {
          commitAssistant()
          setMessages((prev) => [
            ...prev,
            {
              id: `msg-${Date.now()}-trunc`,
              role: "assistant",
              content: `*[${tRef.current("aiChat.transcript.connectionClosed")}]*`,
              createdAt: new Date().toISOString(),
            },
          ])
        }
      } catch (error) {
        if (!ownsRequest()) return
        // Preserve the partial answer on BOTH paths. This is the SINGLE writer
        // for the stopped case — handleStop only aborts, because having both
        // write appended the same text twice.
        const aborted = owner.abort.signal.aborted || (error as Error).name === "AbortError"
        const hadText = !committed && (fullText || toolCallsRef.current.length > 0)
        commitAssistant()
        if (aborted) {
          if (hadText) {
            setMessages((prev) => [
              ...prev,
              {
                id: `msg-${Date.now()}-stopped`,
                role: "assistant",
                content: `*[${tRef.current("aiChat.transcript.generationStopped")}]*`,
                createdAt: new Date().toISOString(),
              },
            ])
          }
        } else {
          // Local/transport throw — nothing ever reached the server, so there
          // is no code to preserve. Use the shared sentence rather than a
          // fourth hand-written one.
          const transportErr = sanitizeAiError({
            errorCode: "network",
            httpStatus: null,
            technical: error instanceof Error ? error.message : String(error),
          })
          setChatError(transportErr)
          const errMsg: ChatMessage = {
            id: `msg-${Date.now()}-err`,
            role: "assistant",
            content: transcriptErrorLine(transportErr, tRef.current),
            createdAt: new Date().toISOString(),
          }
          setMessages((prev) => [...prev, errMsg])
        }
      } finally {
        // The assistant wrote something, so every cached list the user can see
        // may now be wrong. The app's own mutations invalidate their own keys;
        // a tool call goes straight to the API and bypasses that, which is why
        // a group created from chat did not appear until a manual reload.
        //
        // Invalidate broadly rather than mapping tools to query keys: there are
        // ~1,740 queryKey references across ~20 factories, and the neighbouring
        // hand-written TOOL_NAV_MAP shows where such a map ends up — one module
        // covered out of fifteen. Only ACTIVE queries refetch (the handful on
        // screen); the rest are just marked stale.
        //
        // In `finally` so it survives every exit path, and once per request
        // rather than per tool_result — a turn that writes ten times should
        // refetch after the writes land, not while they are still happening.
        //
        // The chat's OWN keys are excluded: conversations and messages are
        // cached too, and refetching the live conversation as the stream ends
        // races the local streaming state that just committed the answer.
        if (mutatedThisTurn) {
          queryClient.invalidateQueries({
            predicate: (q) => q.queryKey[0] !== "ai-chat",
          })
        }
        if (requestOwnerRef.current === owner) {
          requestOwnerRef.current = null
          setIsStreaming(false)
          setStreamingContent("")
          setStreamingToolCalls([])
          toolCallsRef.current = []
          pendingNavRef.current = null
        }
      }
    },
    // stepUpToken intentionally omitted — sendMessage reads from stepUpTokenRef
    // (synchronous, always current) not from the captured state variable.
    // clearStepUpToken IS listed: it is stable (useCallback with []), so it adds
    // no re-creation churn, and listing it keeps exhaustive-deps honest.
    [
      activeId,
      router,
      pathname,
      autoNavigate,
      clearStepUpToken,
      queryClient,
    ]
  )

  // ── Stop streaming ───────────────────────────────────────────────────────
  const handleStop = useCallback(() => {
    // Abort ONLY. The stream's catch block commits the partial answer and
    // appends the "[Generation stopped]" note. Writing here as well produced
    // the answer twice once the abort path started preserving it.
    requestOwnerRef.current?.abort.abort()
  }, [])

  // ── Regenerate ───────────────────────────────────────────────────────────
  const handleRegenerate = useCallback(() => {
    if (requestOwnerRef.current) return
    const lastUserIdx = [...messages].reverse().findIndex((m) => m.role === "user")
    if (lastUserIdx === -1) return
    const idx = messages.length - 1 - lastUserIdx
    const lastUserMsg = messages[idx]
    setMessages(messages.slice(0, idx))
    sendMessage(lastUserMsg.content)
  }, [messages, sendMessage])

  // ── Edit & resend ────────────────────────────────────────────────────────
  const handleEditMessage = useCallback(
    (messageId: string) => {
      if (requestOwnerRef.current) return
      const msgIdx = messages.findIndex((m) => m.id === messageId)
      if (msgIdx === -1) return
      const msg = messages[msgIdx]
      setInputValue(msg.content)
      if (activeId) {
        fetchApi(`/ai-chat/conversations/${activeId}`, {
          method: "PATCH",
          body: JSON.stringify({}),
        }).catch(() => {/* silent */})
      }
      setMessages(messages.slice(0, msgIdx))
    },
    [messages, activeId]
  )

  // ── Conversation actions ─────────────────────────────────────────────────
  const handleRename = useCallback(
    async (title: string) => {
      if (!activeId || requestOwnerRef.current) return
      await fetchApi(`/ai-chat/conversations/${activeId}`, {
        method: "PATCH",
        body: JSON.stringify({ title }),
      })
      if (!requestOwnerRef.current) fetchConversations()
    },
    [activeId, fetchConversations]
  )

  const handleTogglePin = useCallback(
    async (id?: string) => {
      if (requestOwnerRef.current) return
      const targetId = id || activeId
      if (!targetId) return
      const conv = conversations.find((c) => c.id === targetId)
      if (!conv) return
      await fetchApi(`/ai-chat/conversations/${targetId}`, {
        method: "PATCH",
        body: JSON.stringify({ isPinned: !conv.isPinned }),
      })
      if (!requestOwnerRef.current) fetchConversations()
    },
    [activeId, conversations, fetchConversations]
  )

  const handleToggleStar = useCallback(
    async (id?: string) => {
      if (requestOwnerRef.current) return
      const targetId = id || activeId
      if (!targetId) return
      const conv = conversations.find((c) => c.id === targetId)
      if (!conv) return
      await fetchApi(`/ai-chat/conversations/${targetId}`, {
        method: "PATCH",
        body: JSON.stringify({ isStarred: !conv.isStarred }),
      })
      if (!requestOwnerRef.current) fetchConversations()
    },
    [activeId, conversations, fetchConversations]
  )

  const handleDelete = useCallback(
    async (id?: string) => {
      if (requestOwnerRef.current) return
      const targetId = id || activeId
      if (!targetId) return
      await fetchApi(`/ai-chat/conversations/${targetId}`, { method: "DELETE" })
      if (requestOwnerRef.current) return
      if (targetId === activeId) {
        setActiveId(null)
        setMessages([])
      }
      fetchConversations()
    },
    [activeId, fetchConversations]
  )

  const handleExport = useCallback(() => {
    if (!messages.length) return
    const activeConv = conversations.find((c) => c.id === activeId)
    const md = messages
      .map((m) => `**${m.role === "user" ? "You" : "Assistant"}:**\n\n${m.content}`)
      .join("\n\n---\n\n")
    const blob = new Blob([md], { type: "text/markdown" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = exportFileName(activeConv?.title)
    a.click()
    URL.revokeObjectURL(url)
  }, [messages, conversations, activeId])

  const handleCopyAll = useCallback(() => {
    const text = messages
      .map((m) => `${m.role === "user" ? "You" : "Assistant"}: ${m.content}`)
      .join("\n\n")
    navigator.clipboard.writeText(text)
  }, [messages])

  const askInScope = useCallback(
    async (text: string, request: AskScopeRequest) => {
      // Opening the panel is never refused: it is where a failure explains itself.
      setCompanionMode("drawer")
      if (!text.trim() || requestOwnerRef.current) return
      askScopeRef.current = request
      setActiveId(null)
      setMessages([])
      setStreamingContent("")
      setStreamingToolCalls([])
      await sendMessage(text, { fresh: true })
    },
    [sendMessage, setActiveId],
  )

  const handleSuggestion = useCallback(
    (text: string) => {
      sendMessage(text)
    },
    [sendMessage]
  )

  // ── Verification callbacks ───────────────────────────────────────────────
  //
  // The approval token minted by the dialog (confirm-tier or step-up) is
  // bound server-side to (toolName, hash(input)) — see
  // StepUpService.checkBinding / AiToolExecutorService.enforceBinding in
  // apps/api. This USED TO re-prompt the MODEL in natural language to
  // reproduce the exact call it had just been blocked on
  // (`"call ${toolName} ... with EXACTLY these arguments: ${JSON.stringify(...)}"`),
  // which was wrong twice over: (1) any drift the model introduced while
  // re-deriving the call — reworded free text, a regenerated id, a different
  // row limit — produced a different input hash, and the executor answered
  // APPROVAL_MISMATCH, dead-ending a user who had just approved the action;
  // (2) that instruction rendered as the USER'S OWN chat bubble, permanently
  // exposing the raw tool id and a JSON argument dump in their transcript.
  //
  // `POST /ai-chat/tools/execute-approved` (ai-chat.controller.ts) runs the
  // approved (toolName, input) directly through the SAME
  // `AiToolExecutorService.executeTool` a model-issued call runs — no model
  // round-trip, so no re-derivation to drift, and no synthesized message
  // ever reaches sendMessage. The server narrates the outcome itself
  // (tools/tool-narration.ts) and returns the finished sentence; this only
  // appends it as an assistant message alongside a normal tool-result chip.
  const handleVerified = useCallback(async () => {
    setVerificationOpen(false)
    if (requestOwnerRef.current) return

    const toolName = pendingVerificationTool
    const toolLabel = pendingVerificationToolLabel || resolveToolLabel(toolName)
    const input = pendingVerificationInputRef.current
    const conversationId = activeId
    // Read from the ref (not captured state) — written synchronously by
    // setStepUpToken on the same tick the dialog calls this, before any
    // render flush. Mirrors sendMessage's own stepUpTokenRef read above.
    const approvalToken = stepUpTokenRef.current

    if (!conversationId || !approvalToken) {
      const aiErr = toAiError(
        // The sentence is the catalogue's; tRef is the translator of the render on screen.
        new Error(tRef.current("aiChat.approvalMissing")),
      )
      setChatError(aiErr)
      return
    }

    // Single-use — burn it locally too, mirroring sendMessage's own token
    // clearing. The server independently enforces this (Redis-backed
    // single-use burn); this just keeps a spent token out of client state.
    clearStepUpToken()

    const owner: ChatRequestOwner = {
      token: Symbol("execute-approved"),
      abort: new AbortController(),
    }
    requestOwnerRef.current = owner
    setIsStreaming(true)
    const executingTc: StreamingToolCall = { toolName, toolLabel, status: "executing" }
    toolCallsRef.current = [executingTc]
    setStreamingToolCalls([executingTc])

    try {
      const response = await fetchApi<{
        success: boolean
        toolName: string
        toolLabel: string
        content: string
        /**
         * The card for what the call created or started (#38) — the same
         * whitelisted card the streaming path sends. Absent from an older API.
         */
        entity?: EntityCard | null
      }>("/ai-chat/tools/execute-approved", {
        method: "POST",
        body: JSON.stringify({ toolName, input, stepUpToken: approvalToken, conversationId }),
        headers: ((): Record<string, string> => { const context = vishingChatContext(pathname, window.location.search, useWorkspaceStore.getState().activeDomainId); return context?.domainId ? { 'x-domain-id': context.domainId } : {}; })(),
        signal: owner.abort.signal,
      })

      if (requestOwnerRef.current !== owner) return

      const assistantMsg: ChatMessage = {
        id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        role: "assistant",
        content: response.content,
        toolCalls: [
          {
            id: `tc-${Date.now()}`,
            toolName: response.toolName,
            toolLabel: response.toolLabel,
            input: {},
            status: response.success ? "Completed" : "error",
            result: { success: response.success },
            // A confirmed deck generation carries where its deck will be
            // (#38); the message renders it under the reply.
            entity: response.entity ?? null,
          },
        ],
        createdAt: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, assistantMsg])
    } catch (error) {
      if (requestOwnerRef.current !== owner) return
      const aiErr = toAiError(error)
      setChatError(aiErr)
      setMessages((prev) => [
        ...prev,
        {
          id: `msg-${Date.now()}-err`,
          role: "assistant",
          content: transcriptErrorLine(aiErr, tRef.current),
          createdAt: new Date().toISOString(),
        },
      ])
    } finally {
      if (requestOwnerRef.current === owner) {
        requestOwnerRef.current = null
        setIsStreaming(false)
        setStreamingToolCalls([])
        toolCallsRef.current = []
      }
    }
  }, [pendingVerificationTool, pendingVerificationToolLabel, activeId, clearStepUpToken, pathname])

  const handleVerificationCancel = useCallback(() => {
    setVerificationOpen(false)
    setPendingVerificationTool("")
    sendMessage("I cancelled the verification. Do not proceed with the destructive operation.")
  }, [sendMessage])

  // ── Context value ────────────────────────────────────────────────────────
  // Memoised so the identity only changes when something in it actually
  // changed. Every handler below is already a `useCallback` and the rest are
  // `useState` setters (stable by React's guarantee), so this genuinely holds
  // across renders — without it the object literal was new on EVERY render and
  // no consumer could ever skip one.
  const value: ChatContextValue = useMemo(
    () => ({
        conversations,
      activeId,
      fetchConversations,
      loadConversation,
      handleNewChat,
      handleDelete,
      handleRename,
      handleTogglePin,
      handleToggleStar,

      messages,
      inputValue,
      setInputValue,
      sendMessage,
      askInScope,
      handleStop,
      handleRegenerate,
      handleEditMessage,

      isStreaming,
      streamingContent,
      streamingToolCalls,

      handleExport,
      handleCopyAll,
      handleSuggestion,
      apiKeyMissing,
      chatError,
      clearChatError,

      verificationOpen,
      pendingVerificationTool,
      pendingVerificationToolLabel,
      pendingVerificationInputRef,
      pendingVerificationRequirement,
      pendingVerificationFactors,
      pendingVerificationReason,
      pendingVerificationMessage,
      handleVerified,
      handleVerificationCancel,

      chatSidebarOpen,
      setChatSidebarOpen,

      companionMode,
      setCompanionMode,

      drawerWidth,
      setDrawerWidth,

      autoNavigate,
      setAutoNavigate,

      stepUpToken,
      stepUpTokenExpiresAt,
      setStepUpToken,
      clearStepUpToken,
    }),
    [
      conversations,
      activeId,
      fetchConversations,
      loadConversation,
      handleNewChat,
      handleDelete,
      handleRename,
      handleTogglePin,
      handleToggleStar,
      messages,
      inputValue,
      setInputValue,
      sendMessage,
      askInScope,
      handleStop,
      handleRegenerate,
      handleEditMessage,
      isStreaming,
      streamingContent,
      streamingToolCalls,
      handleExport,
      handleCopyAll,
      handleSuggestion,
      apiKeyMissing,
      chatError,
      clearChatError,
      verificationOpen,
      pendingVerificationTool,
      pendingVerificationToolLabel,
      pendingVerificationInputRef,
      pendingVerificationRequirement,
      pendingVerificationFactors,
      pendingVerificationReason,
      pendingVerificationMessage,
      handleVerified,
      handleVerificationCancel,
      chatSidebarOpen,
      setChatSidebarOpen,
      companionMode,
      setCompanionMode,
      drawerWidth,
      setDrawerWidth,
      autoNavigate,
      setAutoNavigate,
      stepUpToken,
      stepUpTokenExpiresAt,
      setStepUpToken,
      clearStepUpToken,
    ],
  )

  // Narrow value for the page layouts. Changes only when the drawer actually
  // moves — not when someone types in the composer or a token streams in.
  const layoutValue: ChatLayoutValue = useMemo(
    () => ({ companionMode, drawerWidth }),
    [companionMode, drawerWidth],
  )

  return (
    <ChatContext.Provider value={value}>
      <ChatLayoutContext.Provider value={layoutValue}>
        {children}
      </ChatLayoutContext.Provider>
    </ChatContext.Provider>
  )
}
